import { useState, useEffect, useCallback } from 'react';
import {
  ADMIN_APP_ROLES,
  AuthState,
  landingRouteForRole,
  LoginRequest,
} from '../types/auth.types';
import { AuthService } from '../services/auth.service';
import { StorageService } from '../services/storage.service';

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  expiresIn: null,
  isLoading: true,
  error: null,
};

export const useAuth = (navigate?: (path: string, options?: { replace?: boolean }) => void) => {
  const [authState, setAuthState] = useState<AuthState>(initialState);

  const initializeAuth = useCallback(async () => {
    try {
      const isAuthenticated = AuthService.isAuthenticated();
      
      if (isAuthenticated) {
        // Validar y refrescar el token si es necesario
        try {
          const isValid = await AuthService.validateAndRefreshAuth();
          if (!isValid) {
            // Si la validación falla, limpiar el estado y redirigir al login
            setAuthState({
              ...initialState,
              isLoading: false,
            });
            if (navigate) {
              navigate('/auth/login', { replace: true });
            }
            return;
          }
        } catch (error) {
          console.error('Auth validation failed during initialization:', error);
          setAuthState({
            ...initialState,
            isLoading: false,
            error: 'Authentication validation failed',
          });
          if (navigate) {
            navigate('/auth/login', { replace: true });
          }
          return;
        }
      }

      const user = StorageService.getUser();
      const accessToken = StorageService.getAccessToken();
      const refreshToken = StorageService.getRefreshToken();
      const expiresIn = StorageService.getExpiresIn();

      // Validar que el usuario tenga un rol con acceso al panel (ver ADMIN_APP_ROLES).
      if (user && !ADMIN_APP_ROLES.includes(user.role)) {
        // Limpiar los datos de autenticación si el rol no es permitido
        AuthService.logout();
        setAuthState({
          ...initialState,
          isLoading: false,
          error: 'No tiene permisos para acceder a esta aplicación. Solo usuarios con rol de administración pueden acceder.',
        });
        if (navigate) {
          navigate('/auth/login', { replace: true });
        }
        return;
      }

      setAuthState({
        isAuthenticated: isAuthenticated && !!accessToken && !!refreshToken,
        user,
        accessToken,
        refreshToken,
        expiresIn,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Auth initialization error:', error);
      setAuthState({
        ...initialState,
        isLoading: false,
        error: 'Failed to initialize authentication',
      });
      if (navigate) {
        navigate('/auth/login', { replace: true });
      }
    }
  }, [navigate]);

  const login = useCallback(async (credentials: LoginRequest) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await AuthService.login(credentials);
      
      // Validar que el usuario tenga un rol con acceso al panel (ver ADMIN_APP_ROLES).
      if (!ADMIN_APP_ROLES.includes(response.user.role)) {
        // Limpiar los datos de autenticación que se guardaron
        AuthService.logout();
        
        const errorMessage = 'No tiene permisos para acceder a esta aplicación.';
        setAuthState(prev => ({
          ...prev,
          isAuthenticated: false,
          user: null,
          accessToken: null,
          refreshToken: null,
          expiresIn: null,
          isLoading: false,
          error: errorMessage,
        }));
        
        throw new Error(errorMessage);
      }
      
      setAuthState({
        isAuthenticated: true,
        user: response.user,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
        isLoading: false,
        error: null,
      });
      
      // Aterrizar según el ROL (no siempre en /users): un rol que no
      // administra usuarios —p.ej. INDICADORES— se topaba con un 403 nada más
      // entrar. Ver `landingRouteForRole`.
      if (navigate) {
        navigate(landingRouteForRole(response.user.role), { replace: true });
      }
      
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [navigate]);

  const logout = useCallback(() => {
    AuthService.logout();
    setAuthState(initialState);
    
    // Redirigir automáticamente a /auth/login
    if (navigate) {
      navigate('/auth/login', { replace: true });
    }
  }, [navigate]);

  // Función para validar la autenticación periódicamente
  const validateAuth = useCallback(async () => {
    if (!authState.isAuthenticated) return;

    try {
      const isValid = await AuthService.validateAndRefreshAuth();
      if (!isValid) {
        logout();
      }
    } catch (error) {
      console.error('Periodic auth validation failed:', error);
      logout();
    }
  }, [authState.isAuthenticated, logout]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Validar la autenticación cada 5 minutos si está autenticado
  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const interval = setInterval(validateAuth, 5 * 60 * 1000); // 5 minutos
    
    return () => clearInterval(interval);
  }, [authState.isAuthenticated, validateAuth]);

  return {
    ...authState,
    login,
    logout,
    validateAuth,
  };
}; 